import React, { useRef, useEffect, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import {
  Box,
  Button,
  Paper,
  Typography,
  useTheme,
  Stack,
} from '@mui/material';
import { Delete as DeleteIcon, Save as SaveIcon } from '@mui/icons-material';

interface SignaturePadProps {
  value?: string;
  onChange: (signature: string) => void;
  width?: number;
  height?: number;
}

export const SignaturePad: React.FC<SignaturePadProps> = ({
  value,
  onChange,
  width = 500,
  height = 200,
}) => {
  const theme = useTheme();
  const signaturePadRef = useRef<SignatureCanvas>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [canvasSize, setCanvasSize] = useState({ width, height });

  useEffect(() => {
    if (value && signaturePadRef.current) {
      signaturePadRef.current.fromDataURL(value);
    }
  }, [value]);

  useEffect(() => {
    const updateCanvasSize = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        const aspectRatio = height / width;
        const newHeight = containerWidth * aspectRatio;
        
        setCanvasSize({
          width: containerWidth,
          height: newHeight
        });
      }
    };

    updateCanvasSize();
    window.addEventListener('resize', updateCanvasSize);
    
    return () => {
      window.removeEventListener('resize', updateCanvasSize);
    };
  }, [width, height]);

  const handleClear = () => {
    if (signaturePadRef.current) {
      signaturePadRef.current.clear();
      onChange('');
    }
  };

  const handleSave = () => {
    if (signaturePadRef.current && !signaturePadRef.current.isEmpty()) {
      const signature = signaturePadRef.current.toDataURL();
      onChange(signature);
    }
  };

  return (
    <Box>
      <Typography variant="subtitle1" gutterBottom>
        Unterschrift
      </Typography>
      <Paper
        ref={containerRef}
        elevation={0}
        sx={{
          border: `2px solid ${theme.palette.divider}`,
          borderRadius: 2,
          overflow: 'hidden',
          width: '100%',
        }}
      >
        <SignatureCanvas
          ref={signaturePadRef}
          canvasProps={{
            width: canvasSize.width,
            height: canvasSize.height,
            className: 'signature-canvas',
            style: {
              width: canvasSize.width,
              height: canvasSize.height,
              backgroundColor: theme.palette.background.paper,
              display: 'block',
            },
          }}
        />
      </Paper>
      <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
        <Button
          variant="outlined"
          color="error"
          startIcon={<DeleteIcon />}
          onClick={handleClear}
        >
          Löschen
        </Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={<SaveIcon />}
          onClick={handleSave}
        >
          Speichern
        </Button>
      </Stack>
      <style>
        {`
          .signature-canvas {
            touch-action: none;
          }
        `}
      </style>
    </Box>
  );
}; 